import { Body, SectionTitle } from "@sprout/typography.tsx";
import type Application from "../../types/application";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import useTranslation from "@hooks/use_translation.ts";
import { router } from "@inertiajs/react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useShowFlash } from "@/sprout/flash";
import { DestructiveButton, SecondaryButton } from "@sprout/buttons";

type Props = {
  application: Application;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const MarkInactiveModal = ({ application, open, onOpenChange }: Props) => {
  const { t } = useTranslation("applications");
  const showFlash = useShowFlash();
  const queryClient = useQueryClient();

  const handleMarkInactiveConfirm = async () => {
    try {
      await axios.patch(`/applications/${application.id}/mark_inactive`);
      await queryClient.invalidateQueries({ queryKey: ["applications"] });
      showFlash(t("marked_as_inactive_success"));
      router.visit("/applications");
    } catch (error) {
      showFlash(t("marked_as_inactive_error"), "error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <SectionTitle>{t("inactive_modal_title")}</SectionTitle>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Body>{t("inactive_modal_body")}</Body>
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <SecondaryButton>{t("inactive_modal_cancel")}</SecondaryButton>
          </DialogClose>
          <DestructiveButton onClick={handleMarkInactiveConfirm}>
            {t("inactive_modal_confirm")}
          </DestructiveButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
